import useAccount from '../useAccount';
import { Notify } from '../../Utils/React';
import useStore from '@tools/Store/useStore';
import { useCallback, useState } from 'react';
import useLoading from '../useLoading/useLoading';
import axios, { AxiosRequestConfig } from 'axios';
import { CONFIG } from '../../../App/Config/constants';
import { setUploadProgress } from '@tools/Store/actions/DashboardActions';

//? ----------------- 👇 Initial data ------------------------------------

const ReqInit: RequestOptions = { base: true, throwError: false };

//? -------------------- 👇 useFetch -------------------------------------

const useFetch = <T extends object | any = any>(fetchUrl?: string, fetchOptions?: RequestOptions) => {
	const loadings = useLoading();
	const { dispatch } = useStore();
	const { user, tokenRef } = useAccount();
	const [data, setData] = useState<null | T>(null);
	const [error, setError] = useState<boolean | any>(false);

	const setLoadingByMethod = (isLoading: boolean, method?: RequestMethods) => {
		loadings.set('all', isLoading);
		if (method) loadings.set(method, isLoading);
	};

	const onUploadProgress: OnUploadProgress = (event, percentage) => {
		dispatch(setUploadProgress(percentage));
		fetchOptions?.onUploadProgress?.(event, percentage);
	};

	const fetchByMethod = useCallback(
		(method: RequestMethods) => async (options?: RequestOptions) => {
			let json;

			setData(null);
			setError(false);
			setLoadingByMethod(true, method);

			const mergedOptions = { ...fetchOptions, ...options, onUploadProgress } as RequestOptions;
			const url = mergedOptions?.url || fetchUrl;
			if (!url) return;

			try {
				const ext_options = { ...mergedOptions, token: tokenRef?.current, method: method };
				const { json: res_json } = await authFetch(url, ext_options);
				json = res_json;
				setData(json);
				setLoadingByMethod(false, method);
				if (json?.error) {
					const errorMsg =
						json?.message ||
						(typeof json?.error === 'string' ? json.error : json?.error?.message) ||
						'Request failed';
					throw new Error(errorMsg);
				}
			} catch (e) {
				setError(e);
				setData(null);
				setLoadingByMethod(false, method);
				throw e;
			}

			return json as T;
		},
		[fetchOptions, fetchUrl, CONFIG, user]
	);

	const LOADING = {
		loading: loadings.is('all'),
		getLoading: loadings.is('GET'),
		putLoading: loadings.is('PUT'),
		postLoading: loadings.is('POST'),
		deleteLoading: loadings.is('DELETE'),
		loadings: {
			all: loadings.is('all'),
			get: loadings.is('GET'),
			put: loadings.is('PUT'),
			post: loadings.is('POST'),
			delete: loadings.is('DELETE'),
		},
	};

	const FETCH_METHODS = {
		Get: fetchByMethod('GET'),
		Put: fetchByMethod('PUT'),
		Post: fetchByMethod('POST'),
		Delete: fetchByMethod('DELETE'),
	};

	return { data, error, ...LOADING, ...FETCH_METHODS };
};

//? --------------------- 👇 Utils ---------------------------------------

const extendOptions = (options: RequestOptions) => ({ ...ReqInit, ...options });

export const authFetch = async (url: string, options?: RequestOptions) => {
	let { token = window?.user_token, body } = options || {};

	let response: Response | undefined;
	let json: any = null;

	if (!navigator.onLine) {
		const msg =
			'You are offline. Please check your internet connection.';

		Notify.error(msg, {
			duration: 4000,
			icon: 'l-wifi-slash',
		});

		const err: any = new Error(msg);
		err.status = 0;
		err.offline = true;

		throw err;
	}

	// -----------------------------
	// Handle body (JSON / FormData)
	// -----------------------------
	const isFormData = body instanceof FormData;

	if (body && typeof body === 'object' && !isFormData) {
		body = JSON.stringify(body);
	}

	// -----------------------------
	// Build options
	// -----------------------------
	const fetchOptions = extendOptions({
		...options,
		body,
	});

	fetchOptions.headers = {
		...fetchOptions.headers,
		...(token ? { Authorization: 'Bearer ' + token } : {}),
		...(!isFormData
			? { 'Content-Type': 'application/json' }
			: {}), // let browser set multipart boundary
	};

	// -----------------------------
	// Query params
	// -----------------------------
	const params = fetchOptions?.params;

	let stringParam = '';

	if (params) {
		const entries = Object.entries(params)
			.filter(([, v]) => v !== undefined)
			.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);

		if (entries.length) {
			stringParam = `?${entries.join('&')}`;
		}
	}

	// -----------------------------
	// Final URL
	// -----------------------------
	const req_url =
		(fetchOptions.base ? CONFIG.SERVER : '') +
		url +
		stringParam;

	// -----------------------------
	// REQUEST EXECUTION
	// -----------------------------
	try {
		if (!isFormData) {
			response = await fetch(
				req_url,
				fetchOptions as RequestInit
			);

			// ALWAYS try to read JSON safely
			try {
				json = await response.json();
			} catch {
				json = null;
			}

			// -----------------------------
			// IMPORTANT: unified error model
			// -----------------------------
			if (!response.ok) {
				const rawError =
					json?.message ||
					(typeof json?.error === 'string' ? json.error : json?.error?.message);

				const errorMsg =
					rawError && !/^\s*(request failed \()?500\)?\s*$/i.test(rawError)
						? rawError
						: response.status >= 500
						? 'A server error occurred. Please try again.'
						: `Request failed (${response.status})`;

				const error: any = new Error(errorMsg);

				error.status = response.status;
				error.data = json;
				error.response = response;

				throw error;
			}
		} else {
			const onUploadProgress = (event: ProgressEvent) => {
				const percentage = Math.round(
					(100 * event.loaded) / event.total
				);

				options?.onUploadProgress?.(
					event,
					percentage
				);
			};

			const axios_options: AxiosRequestConfig = {
				url: req_url,
				data: fetchOptions?.body,
				...((fetchOptions as any) || {}),
				onUploadProgress,
			};

			const res = await axios.request(axios_options);

			json = res.data;
			response = undefined as any;
		}
	} catch (e: any) {
		// -----------------------------
		// Normalize thrown errors too
		// -----------------------------
		if (e?.response?.data) {
			const serverMsg =
				e.response.data?.message ||
				(typeof e.response.data?.error === 'string'
					? e.response.data.error
					: e.response.data?.error?.message);
			if (serverMsg && !/^\s*(request failed \()?500\)?\s*$/i.test(serverMsg)) {
				e.message = serverMsg;
			} else if (e?.response?.status >= 500) {
				e.message = 'A server error occurred. Please try again.';
			}
			e.data = e.response.data;
		} else if ((e?.response?.status >= 500 || e?.status >= 500) && (!e.message || /500/.test(e.message))) {
			e.message = 'A server error occurred. Please try again.';
		}
		if (e?.status === undefined) {
			e.status = e?.response?.status ?? 0;
		}

		throw e;
	}

	return {
		json,
		response,
		status: response?.status,
		ok: response?.ok,
	};
};

const authFetchByMethod = (method: RequestMethods) => async (url: string, options?: RequestOptions) =>
	await authFetch(url, { method: method, ...options });

authFetch.get = authFetchByMethod('GET');
authFetch.put = authFetchByMethod('PUT');
authFetch.post = authFetchByMethod('POST');
authFetch.delete = authFetchByMethod('DELETE');

export const objectToFormData = (obj: { [key: string]: string | Blob | undefined }) => {
	const formData = new FormData();
	Object.keys(obj || {}).forEach(key => obj[key] && formData.append(key, obj[key]!!));
	return formData;
};

//? ----------------------- 👇 Types ------------------------------------

export type RequestMethods = 'GET' | 'POST' | 'PUT' | 'DELETE';

export type OnUploadProgress = (event: ProgressEvent, percentage: number) => void;

export type RequestOptions = Omit<RequestInit, 'body'> & {
	url?: string;
	base?: boolean;
	token?: string;
	params?: object;
	throwError?: boolean;
	body?: object | string | BodyInit;
	onUploadProgress?: OnUploadProgress;
};

//? ----------------------- 👇 Export ------------------------------------

export default useFetch;
